/**
 * Der Steckbrief eines Standorts im Mini-Dashboard: je Gewerk das Ist eines Jahres gegen die Spanne des
 * Benchmarks, mit Befund (tri-server docs/fm-baselining-entwurf.md §9.3). Erscheint, sobald der gewählte
 * Knoten einen Standort hat; mit gewählter Leistungsebene nur deren Gewerke.
 *
 * Ein Befund trägt immer Symbol und Text, nie die Farbe allein; die Statusfarben sind dem Befund vorbehalten.
 * Ein Klick auf das Symbol nennt die möglichen Ursachen — ein Befund ist ein Verdacht, kein Beweis. Hat der Server
 * Positionen gefunden, die nach ihrer Anlage woandershin gehören, stehen sie davor, mit dem Befund nach der Korrektur.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { AlertTriangle, ArrowDown, ArrowUp, CheckCircle2, CircleHelp, Minus, Package } from "lucide-react";

import { authenticationAtom } from "../../State";
import { useBaseliningAPI, type BenchmarkFinding, type BenchmarkRow, type CatalogueNode, type SiteBenchmark } from "../../BaseliningAPI";
import { NONE, formatEuro } from "./pivotModel";
import DetailSection from "./DetailSection";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const FINDING_STYLE: Record<BenchmarkFinding, { icon: typeof CheckCircle2; className: string }> = {
  inBand: { icon: CheckCircle2, className: "text-[#0ca30c]" },
  aboveBand: { icon: ArrowUp, className: "text-[#ec835a]" },
  belowBand: { icon: ArrowDown, className: "text-[#fab219]" },
  missingSpend: { icon: AlertTriangle, className: "text-[#d03b3b]" },
  noQuantity: { icon: CircleHelp, className: "text-muted-foreground" },
  bundled: { icon: Package, className: "text-muted-foreground" },
  coveredByBundle: { icon: Minus, className: "text-muted-foreground" },
};

const flatten = (nodes: CatalogueNode[]): CatalogueNode[] => nodes.flatMap((n) => [n, ...flatten(n.children)]);

/** Pure: die Zeilen eines Steckbriefs, auf eine Leistungsebene eingeschränkt (`root`, z. B. „cleaning“). */
export const rowsWithin = <T extends { node: string }>(rows: T[], root?: string | null): T[] =>
  !root || root === NONE ? rows : rows.filter((r) => r.node === root || r.node.startsWith(`${root}.`) || root.startsWith(`${r.node}.`));

/** Pure: der Text eines Befunds. Nur eine Prüfung ist eine Pflicht, Wartung hat den Marktsatz je Anlage hinter sich. */
export const findingLabelKey = (finding: BenchmarkFinding, activity: string | null): string =>
  finding === "missingSpend" ? `missingSpend.${activity === "statutoryInspection" ? "inspection" : activity === "maintenance" ? "maintenance" : "other"}` : finding;

/** Pure: die möglichen Ursachen eines Befunds, als Schlüssel unter `baselining.benchmark.causes`, wahrscheinlichste zuerst. */
export const findingCauses = (finding: BenchmarkFinding, activity: string | null): string[] => {
  switch (finding) {
    case "missingSpend":
      return [activity === "statutoryInspection" ? "notDoneInspection" : "notDone", "invoicesMissing", "misclassifiedElsewhere", "paidElsewhere"];
    case "aboveBand":
      return ["priceAboveMarket", "moreScope", "misclassifiedHere", "quantitiesTooSmall", "oneOff"];
    case "belowBand":
      return ["invoicesMissing", "lessScope", "partialPeriod", "misclassifiedElsewhere", "quantitiesTooLarge"];
    case "bundled":
      return ["bundleScope"];
    case "noQuantity":
      return ["quantitiesMissing", "misclassifiedHere"];
    default:
      return [];
  }
};

/** Pure: wie viele Positionen einer Zeile falsch eingeordnet sind, in beide Richtungen. */
export const misfiledCount = (row: Pick<BenchmarkRow, "misfiled">): number => (row.misfiled ? row.misfiled.incoming.length + row.misfiled.outgoing.length : 0);

export default function BenchmarkPanel({ site, root, period }: { site: string; root?: string | null; period: { from?: string; to?: string } }) {
  const { t, i18n } = useTranslation();
  const [authentication] = useAtom(authenticationAtom) as any;
  const api = useBaseliningAPI(authentication?.customer ?? "");
  const [data, setData] = useState<SiteBenchmark | null | undefined>(undefined);
  const [names, setNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.benchmark(site, period.from && period.to ? period : null), api.getCatalogue()])
      .then(([b, c]) => {
        if (cancelled) return;
        setData(b);
        setNames(Object.fromEntries(flatten(c.nodes).map((n) => [n.key, n.name])));
      })
      .catch((e) => !cancelled && setError(e?.message ?? t("baselining.analysis.loadFailed")));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site, period.from, period.to]);

  const title = t("baselining.benchmark.title");
  if (error) return <DetailSection title={title}><Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert></DetailSection>;
  if (data === undefined) return <DetailSection title={title}><p className="text-xs text-muted-foreground">{t("baselining.analysis.loading")}</p></DetailSection>;
  if (data === null) return <DetailSection title={title}><p className="text-xs text-muted-foreground">{t("baselining.benchmark.none")}</p></DetailSection>;

  const rows = rowsWithin(data.rows, root);
  const euro = (n: number | null) => (n === null ? "—" : formatEuro(n, i18n.language));

  return (
    <DetailSection title={title} aside={t("baselining.benchmark.period", { from: data.period.from, to: data.period.to })}>
      {/* Zwei Zeilen je Gewerk statt einer Tabelle: Das Mini-Dashboard ist schmal, vier Spalten brachen dort um. */}
      <ul className="divide-y rounded-md border text-xs">
        {rows.map((r) => {
          const style = FINDING_STYLE[r.finding];
          const Icon = style.icon;
          const muted = r.finding === "coveredByBundle";
          const causes = findingCauses(r.finding, r.activity);
          const misfiled = misfiledCount(r);
          return (
            <li key={`${r.node}:${r.activity}`} className={`px-2 py-1.5 ${muted ? "text-muted-foreground" : ""}`} title={[r.note, ...(r.band?.basis ?? []), ...(r.band?.missing ?? [])].filter(Boolean).join(" · ")}>
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0">
                  <span className="font-medium">{names[r.node] ?? r.node}</span>
                  {r.activity && <span className="text-muted-foreground"> · {t(`baselining.benchmark.activity.${r.activity}`, r.activity)}</span>}
                </span>
                <span className="inline-flex shrink-0 items-center gap-1 text-right">
                  {causes.length || misfiled ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="rounded-sm p-0.5 hover:bg-muted" aria-label={t("baselining.benchmark.causesTitle")}>
                          <Icon className={`h-3.5 w-3.5 shrink-0 ${style.className}`} aria-hidden />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-96 text-xs" align="end">
                        {r.misfiled && (
                          <div className="mb-3 space-y-1">
                            <p className="font-medium">{t("baselining.benchmark.misfiled.title")}</p>
                            <ul className="space-y-1">
                              {[...r.misfiled.incoming.map((m) => ({ m, key: "incoming", node: m.filedUnder })), ...r.misfiled.outgoing.map((m) => ({ m, key: "outgoing", node: m.belongsTo }))].map(({ m, key, node }) => (
                                <li key={`${key}:${m.line}`}>
                                  <span className="text-muted-foreground">{t(`baselining.benchmark.misfiled.${key}`, { node: names[node] ?? node })}</span> {m.text}{" "}
                                  <span className="tabular-nums">{euro(m.amount)}</span>
                                  <span className="block text-muted-foreground">{t(`baselining.benchmark.misfiled.evidence.${m.evidence}`, { asset: m.asset, supplier: m.supplier })}</span>
                                </li>
                              ))}
                            </ul>
                            <p>
                              {t("baselining.benchmark.misfiled.corrected", {
                                actual: euro(r.misfiled.corrected.actual),
                                finding: t(`baselining.benchmark.findings.${findingLabelKey(r.misfiled.corrected.finding, r.activity)}`),
                              })}
                            </p>
                          </div>
                        )}
                        {causes.length > 0 && <p className="mb-1 font-medium">{t("baselining.benchmark.causesTitle")}</p>}
                        <ul className="list-disc space-y-1 pl-4">
                          {causes.map((c) => (
                            <li key={c}>{t(`baselining.benchmark.causes.${c}`)}</li>
                          ))}
                        </ul>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Icon className={`h-3.5 w-3.5 shrink-0 ${style.className}`} aria-hidden />
                  )}
                  {t(`baselining.benchmark.findings.${findingLabelKey(r.finding, r.activity)}`)}
                  {r.deviation !== null && (r.finding === "aboveBand" || r.finding === "belowBand" || r.finding === "bundled") && (
                    <span className="text-muted-foreground">({r.deviation > 0 ? "+" : ""}{Math.round(r.deviation * 100)} %)</span>
                  )}
                </span>
              </div>
              <div className="mt-0.5 flex flex-wrap items-baseline justify-between gap-x-3 text-muted-foreground">
                <span className="whitespace-nowrap">
                  {t("baselining.benchmark.actual")} <span className="tabular-nums text-foreground">{euro(r.actual)}</span>
                  {" · "}
                  {t("baselining.benchmark.band")} <span className="tabular-nums">{r.band?.median === null || !r.band ? "—" : `${euro(r.band.low)}–${euro(r.band.high)}`}</span>
                </span>
                {misfiled > 0 && <span>{t("baselining.benchmark.misfiled.hint", { count: misfiled })}</span>}
              </div>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-muted-foreground">
        {t("baselining.benchmark.footer", {
          area: data.quantities.cleaningArea,
          glass: data.quantities.glassArea,
          assets: data.quantities.assets,
          excluded: euro(data.excluded.userDrivenOrProject + data.excluded.repair),
        })}
      </p>
    </DetailSection>
  );
}
